import { UserRole } from '@features/shared/auth/domain/models/authenticated-user.model';
import { isSuperAdminEquivalentRole } from '@features/shared/auth/domain/utils/role.util';

export type ManagedRole = UserRole.Admin | UserRole.ClinicOwner | UserRole.Worker | UserRole.Developer;

export const MANAGED_ROLES: readonly ManagedRole[] = [
  UserRole.Admin,
  UserRole.ClinicOwner,
  UserRole.Worker,
  UserRole.Developer,
];

export function isManagedRole(role: string): role is ManagedRole {
  return (MANAGED_ROLES as readonly string[]).includes(role);
}

export const CLINIC_ASSIGNABLE_ROLES: readonly ManagedRole[] = [
  UserRole.Admin,
  UserRole.ClinicOwner,
  UserRole.Worker,
];

export const PLATFORM_ASSIGNABLE_ROLES: readonly ManagedRole[] = [
  ...CLINIC_ASSIGNABLE_ROLES,
  UserRole.Developer,
];

// Mirrors `SystemRoleNames.SuperAdminEquivalent` in the backend's `ResolveRoleAsync`: Developer is a
// PLATFORM account (permission bypass + platform console), so only a SuperAdmin/Developer actor may
// hand it out. The backend is what enforces that -- it answers 403 to anyone else -- and this
// predicate exists only so the UI does not hide an option the actor is legitimately entitled to
// pick. It is not the access control.
export function assignableRolesFor(actorRole: UserRole | null | undefined): readonly ManagedRole[] {
  return isSuperAdminEquivalentRole(actorRole) ? PLATFORM_ASSIGNABLE_ROLES : CLINIC_ASSIGNABLE_ROLES;
}

export function isAssignableBy(role: string, actorRole: UserRole | null | undefined): boolean {
  return (assignableRolesFor(actorRole) as readonly string[]).includes(role);
}

const EDIT_RANKS: Record<ManagedRole, number> = {
  [UserRole.Worker]: 0,
  [UserRole.Admin]: 1,
  [UserRole.ClinicOwner]: 2,
  [UserRole.Developer]: 3,
};

// Developer is the only rank allowed to edit its own peers; SuperAdmin is deliberately excluded
// here since it never accesses the web panel.
export function canEditLocalUser(actorRole: UserRole | null | undefined, targetRole: ManagedRole): boolean {
  if (actorRole === UserRole.Developer && targetRole === UserRole.Developer) return true;
  if (!actorRole || !isManagedRole(actorRole)) return false;
  return EDIT_RANKS[actorRole] > EDIT_RANKS[targetRole];
}
